import React, { useContext } from 'react';
import { ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import { useRouter } from 'next/router';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { invoicesForMonth, transactionsForMonth } from '../../utils/chart-data';
import { formatCurrency, formatCurrencySI } from '../../utils/numbers';
import ChartTooltip from './ChartTooltip';

// Sales beside payments by month. `scope` narrows the linked lists, to one customer for example.
// With `scale`, a $ axis and gridlines, and each month's two bars wide and touching, so a whole
// card's width reads as six pairs rather than twelve thin bars.
const PairedBars = ({ rows, currency, height = 190, scope = '', scale = false }) => {
    const theme = useContext(ThemeContext);
    const router = useRouter();
    const color = (name) => normalizeColor(name, theme);

    const renderTooltip = ({ active, payload }) => {
        const row = payload?.[0]?.payload;
        if (!active || !row || !(row.sales_amount || row.payment_amount)) return null;
        return (
            <ChartTooltip
                title={row.month}
                lines={[
                    `Sales ${formatCurrency(row.sales_amount, currency)}`,
                    `Payments ${formatCurrency(row.payment_amount, currency)}`,
                ]}
            />
        );
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={rows} barGap={scale ? 3 : 2} barCategoryGap={scale ? '34%' : '30%'} margin={{ top: scale ? 8 : 4, right: 0, left: 0, bottom: 0 }}>
                {scale && <CartesianGrid vertical={false} stroke={color('border-weak')} />}
                <XAxis
                    dataKey="display_month_string"
                    interval={rows.length > 6 ? 'preserveStartEnd' : 0}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: scale ? 14 : 16, fill: color('text-weak') }}
                />
                {scale ? (
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        width={56}
                        tick={{ fontSize: 13, fill: color('text-weak') }}
                        tickFormatter={(value) => formatCurrencySI(value, currency)}
                    />
                ) : <YAxis hide />}
                <Tooltip cursor={false} isAnimationActive={false} content={renderTooltip} />
                <Bar
                    dataKey="sales_amount"
                    fill={color('chart-muted')}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={scale ? undefined : 18}
                    minPointSize={3}
                    isAnimationActive={false}
                    style={{ cursor: 'pointer' }}
                    onClick={(row) => router.push(`${invoicesForMonth(row.month)}${scope}`)}
                />
                <Bar
                    dataKey="payment_amount"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={scale ? undefined : 18}
                    minPointSize={3}
                    isAnimationActive={false}
                    style={{ cursor: 'pointer' }}
                    onClick={(row) => router.push(`${transactionsForMonth(row.month)}${scope}`)}
                >
                    {rows.map((row) => (
                        <Cell key={row.month} fill={color(row.payment_amount > 0 ? 'barchart-primary' : 'chart-muted')} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default PairedBars;
